import React, { useState, useEffect } from 'react';
import { AppStoreButton } from './AppStoreButton';
import { BrandLogo } from './BrandLogo';
import { StorageService } from '../services/storage';
import { useI18n } from '../services/i18n';
import {
  Star,
  Trophy,
  TrendingUp,
  Search,
  Bookmark,
  FolderKanban,
  Menu,
  Globe,
  ChevronDown
} from 'lucide-react';

interface NavbarProps {
  currentRoute: string;
  onNavigate: (route: string) => void;
  onOpenSearch: () => void;
  onOpenSignIn: () => void;
}

export const Navbar: React.FC<NavbarProps> = ({
  currentRoute,
  onNavigate,
  onOpenSearch,
  onOpenSignIn
}) => {
  const { language, setLanguage, t } = useI18n();
  const [watchlistCount, setWatchlistCount] = useState(StorageService.getWatchlist().length);
  const [user, setUser] = useState(StorageService.getUser());
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleWatchlist = () => setWatchlistCount(StorageService.getWatchlist().length);
    const handleUser = () => setUser(StorageService.getUser());

    window.addEventListener('appstatsview_watchlist_updated', handleWatchlist);
    window.addEventListener('appstatsview_user_updated', handleUser);
    return () => {
      window.removeEventListener('appstatsview_watchlist_updated', handleWatchlist);
      window.removeEventListener('appstatsview_user_updated', handleUser);
    };
  }, []);

  const secondaryNavLinks = [
    { label: 'New Apps & Releases', route: '/new-apps', badge: 'Live' },
    { label: 'Launch Radar', route: '/launch-radar', badge: 'Radar' },
    { label: 'Top Charts (US & Global)', route: '/charts', badge: 'Global' },
    { label: t('nav.categories'), route: '/categories' },
    { label: t('nav.markets'), route: '/markets' },
    { label: t('nav.developers'), route: '/developers' },
    { label: t('nav.keywords'), route: '/keywords' },
    { label: t('nav.compare'), route: '/compare' },
    { label: t('nav.aiInsights'), route: '/ai-insights', badge: 'AI' },
    { label: t('nav.pricing'), route: '/pricing' }
  ];

  const handleNavClick = (route: string) => {
    onNavigate(route);
    setMobileMenuOpen(false);
  };

  const isDiscoverActive =
    currentRoute === '/' || currentRoute === '' || currentRoute === '/apps' || currentRoute.startsWith('/app/');
  const isChartsActive = currentRoute === '/charts';
  const isTrendingActive = currentRoute === '/trending';
  const isWatchlistActive = currentRoute === '/watchlist';

  return (
    <header className="sticky top-2 sm:top-3.5 z-40 px-3 sm:px-4 max-w-7xl mx-auto w-full transition-all">
      <nav className="h-16 sm:h-[72px] flex items-center justify-between px-3 sm:px-6 border border-white/80 bg-white/90 backdrop-blur-xl shadow-xs sm:shadow-sm rounded-2xl sm:rounded-3xl">
        {/* Brand & Logo */}
        <div className="flex items-center gap-2 sm:gap-3 shrink-0 min-w-0">
          <BrandLogo onClick={() => handleNavClick('/')} size="sm" className="sm:hidden" />
          <BrandLogo onClick={() => handleNavClick('/')} size="md" className="hidden sm:inline-flex" />
        </div>

        {/* Center / Navigation Bar Button Group: Discover, Top Charts (US & Global), Trending, Search, Watchlist */}
        <div className="hidden md:flex items-center gap-3 lg:gap-6 px-1 sm:px-3">
          {/* 1. Discover */}
          <button
            id="nav-btn-discover"
            type="button"
            onClick={() => handleNavClick('/')}
            className={`flex flex-col items-center justify-center py-1 px-2 rounded-xl transition-all duration-150 active:scale-95 group cursor-pointer ${
              isDiscoverActive
                ? 'text-blue-600 font-semibold'
                : 'text-slate-400 hover:text-slate-600 font-medium'
            }`}
          >
            <Star
              className={`w-5 h-5 sm:w-6 sm:h-6 transition-all duration-150 ${
                isDiscoverActive
                  ? 'text-blue-600 fill-blue-600/15 scale-105'
                  : 'text-slate-400 group-hover:text-slate-600'
              }`}
              strokeWidth={isDiscoverActive ? 2.25 : 1.75}
            />
            <span
              className={`text-[11px] sm:text-xs tracking-tight leading-tight mt-1 ${
                isDiscoverActive ? 'text-blue-600 font-bold' : 'text-slate-500'
              }`}
            >
              Discover
            </span>
          </button>

          {/* 2. Top Charts (US & Global) */}
          <button
            id="nav-btn-top-charts"
            type="button"
            onClick={() => handleNavClick('/charts')}
            className={`flex flex-col items-center justify-center py-1 px-2 rounded-xl transition-all duration-150 active:scale-95 group cursor-pointer ${
              isChartsActive
                ? 'text-blue-600 font-semibold'
                : 'text-slate-400 hover:text-slate-600 font-medium'
            }`}
            title="Top Charts (US & Global)"
          >
            <div className="relative">
              <Trophy
                className={`w-5 h-5 sm:w-6 sm:h-6 transition-all duration-150 ${
                  isChartsActive
                    ? 'text-blue-600 fill-blue-600/15 scale-105'
                    : 'text-slate-400 group-hover:text-slate-600'
                }`}
                strokeWidth={isChartsActive ? 2.25 : 1.75}
              />
            </div>
            <span
              className={`text-[11px] sm:text-xs tracking-tight leading-tight mt-1 whitespace-nowrap ${
                isChartsActive ? 'text-blue-600 font-bold' : 'text-slate-500'
              }`}
            >
              Top Charts <span className="text-[10px] text-slate-400 font-normal hidden lg:inline">(US & Global)</span>
            </span>
          </button>

          {/* 3. Trending */}
          <button
            id="nav-btn-trending"
            type="button"
            onClick={() => handleNavClick('/trending')}
            className={`flex flex-col items-center justify-center py-1 px-2 rounded-xl transition-all duration-150 active:scale-95 group cursor-pointer ${
              isTrendingActive
                ? 'text-blue-600 font-semibold'
                : 'text-slate-400 hover:text-slate-600 font-medium'
            }`}
          >
            <TrendingUp
              className={`w-5 h-5 sm:w-6 sm:h-6 transition-all duration-150 ${
                isTrendingActive
                  ? 'text-blue-600 scale-105'
                  : 'text-slate-400 group-hover:text-slate-600'
              }`}
              strokeWidth={isTrendingActive ? 2.25 : 1.75}
            />
            <span
              className={`text-[11px] sm:text-xs tracking-tight leading-tight mt-1 ${
                isTrendingActive ? 'text-blue-600 font-bold' : 'text-slate-500'
              }`}
            >
              Trending
            </span>
          </button>

          {/* 4. Search */}
          <button
            id="nav-btn-search"
            type="button"
            onClick={onOpenSearch}
            className="flex flex-col items-center justify-center py-1 px-2 rounded-xl text-slate-500 hover:text-slate-800 transition-all duration-150 active:scale-95 group cursor-pointer"
          >
            <Search
              className="w-5 h-5 sm:w-6 sm:h-6 text-slate-500 group-hover:text-slate-800 transition-colors"
              strokeWidth={1.85}
            />
            <span className="text-[11px] sm:text-xs font-medium tracking-tight leading-tight mt-1 text-slate-500 group-hover:text-slate-800">
              Search
            </span>
          </button>

          {/* 5. Watchlist */}
          <button
            id="nav-btn-watchlist"
            type="button"
            onClick={() => handleNavClick('/watchlist')}
            className={`relative flex flex-col items-center justify-center py-1 px-2 rounded-xl transition-all duration-150 active:scale-95 group cursor-pointer ${
              isWatchlistActive
                ? 'text-blue-600 font-semibold'
                : 'text-slate-400 hover:text-slate-600 font-medium'
            }`}
          >
            <div className="relative">
              <Bookmark
                className={`w-5 h-5 sm:w-6 sm:h-6 transition-all duration-150 ${
                  isWatchlistActive
                    ? 'text-blue-600 fill-blue-600/15 scale-105'
                    : 'text-slate-400 group-hover:text-slate-600'
                }`}
                strokeWidth={isWatchlistActive ? 2.25 : 1.75}
              />
              {watchlistCount > 0 && (
                <span className="absolute -top-1 -right-2.5 min-w-[17px] h-[17px] px-1 bg-blue-600 text-white text-[10px] font-bold rounded-full flex items-center justify-center border-2 border-white shadow-xs leading-none">
                  {watchlistCount > 99 ? '99+' : watchlistCount}
                </span>
              )}
            </div>
            <span
              className={`text-[11px] sm:text-xs tracking-tight leading-tight mt-1 ${
                isWatchlistActive ? 'text-blue-600 font-bold' : 'text-slate-500'
              }`}
            >
              Watchlist
            </span>
          </button>
        </div>

        {/* Right Actions: Secondary Hubs Dropdown / Language / Sign In / App Store CTA */}
        <div className="flex items-center gap-2 sm:gap-3 shrink-0">
          {/* Quick Mobile Top Charts Button */}
          <button
            id="nav-mobile-btn-top-charts"
            type="button"
            onClick={() => handleNavClick('/charts')}
            className={`md:hidden flex items-center gap-1.5 px-2.5 py-1.5 rounded-xl border text-xs font-bold transition-all cursor-pointer ${
              isChartsActive
                ? 'bg-blue-50 border-blue-300 text-blue-600'
                : 'bg-slate-50/90 border-slate-200/80 text-slate-700 hover:bg-slate-100'
            }`}
            title="Top Charts (US & Global)"
          >
            <Trophy className="w-3.5 h-3.5 text-amber-500" strokeWidth={2.2} />
            <span className="text-[11px] font-bold">Top Charts</span>
          </button>

          {/* Quick Mobile Search Button */}
          <button
            onClick={onOpenSearch}
            className="md:hidden p-2 rounded-xl text-slate-700 hover:bg-slate-100 transition-colors"
            aria-label="Open Search"
            title="Search Apps"
          >
            <Search className="w-5 h-5" strokeWidth={2} />
          </button>
          {/* Language Toggle */}
          <button
            onClick={() => setLanguage(language === 'en' ? 'es' : 'en')}
            className="hidden sm:flex items-center gap-1 px-2.5 py-1.5 rounded-xl bg-slate-100/90 hover:bg-slate-200 text-slate-700 text-xs font-bold transition-all border border-slate-200/60"
            title="Switch Language / Cambiar Idioma"
          >
            <Globe className="w-3.5 h-3.5 text-slate-500" />
            <span className="uppercase">{language}</span>
          </button>

          {/* Sign In / User Profile */}
          {user ? (
            <div className="relative group">
              <button
                onClick={() => handleNavClick('/watchlist')}
                className="flex items-center gap-2 p-1.5 sm:px-3 sm:py-1.5 rounded-xl bg-slate-100 hover:bg-slate-200 text-xs font-bold text-slate-800 transition-colors"
              >
                <span className="w-6 h-6 rounded-full bg-gradient-to-tr from-blue-600 to-indigo-600 text-white flex items-center justify-center text-[11px] font-bold">
                  {user.name.charAt(0)}
                </span>
                <span className="hidden lg:inline-block">{user.name}</span>
                <span className="text-[10px] bg-blue-600 text-white px-1.5 py-0.2 rounded font-bold">
                  {user.plan}
                </span>
              </button>
            </div>
          ) : (
            <button
              onClick={onOpenSignIn}
              className="text-xs font-semibold text-slate-700 hover:text-slate-950 px-3 py-1.5 rounded-xl hover:bg-slate-100 transition-colors"
            >
              Sign In
            </button>
          )}

          {/* Download Button */}
          <div className="hidden lg:block">
            <AppStoreButton variant="mini" />
          </div>

          {/* Menu for additional tools and sections */}
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="p-2 rounded-xl text-slate-700 hover:bg-slate-100 transition-colors"
            aria-label="Toggle navigation menu"
            title="More Categories & Tools"
          >
            <Menu className="w-5 h-5" strokeWidth={2} />
          </button>
        </div>
      </nav>

      {/* Expanded Tools & Categories Drawer */}
      {mobileMenuOpen && (
        <div className="mt-2 p-4 bg-white/95 backdrop-blur-xl border border-slate-200 rounded-3xl shadow-xl space-y-3 animate-fadeIn">
          <div className="flex items-center justify-between pb-2 border-b border-slate-100">
            <span className="text-xs font-bold text-slate-500 uppercase tracking-wider">
              More Tools & Categories
            </span>
            <button
              onClick={() => setLanguage(language === 'en' ? 'es' : 'en')}
              className="sm:hidden flex items-center gap-1 text-xs font-bold text-slate-600 bg-slate-100 px-2 py-1 rounded-lg"
            >
              <Globe className="w-3 h-3 text-slate-500" />
              <span>{language.toUpperCase()}</span>
            </button>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs font-semibold text-slate-700">
            {secondaryNavLinks.map((item) => (
              <button
                key={item.route}
                onClick={() => handleNavClick(item.route)}
                className={`p-2.5 rounded-xl text-left flex items-center justify-between ${
                  currentRoute === item.route
                    ? 'bg-blue-50 text-blue-600 font-bold'
                    : 'hover:bg-slate-50'
                }`}
              >
                <span>{item.label}</span>
                {item.badge && (
                  <span className="text-[9px] bg-slate-100 text-slate-600 px-1.5 py-0.5 rounded">
                    {item.badge}
                  </span>
                )}
              </button>
            ))}
            <button
              onClick={() => handleNavClick('/collections')}
              className="p-2.5 rounded-xl text-left flex items-center justify-between hover:bg-slate-50"
            >
              <span className="inline-flex items-center gap-1.5">
                <FolderKanban className="w-3.5 h-3.5" strokeWidth={2} />
                <span>Collections</span>
              </span>
            </button>
          </div>

          <div className="pt-3 border-t border-slate-100 flex items-center justify-between">
            <AppStoreButton variant="mini" />
            <button
              onClick={() => {
                onOpenSignIn();
                setMobileMenuOpen(false);
              }}
              className="text-xs font-bold text-blue-600"
            >
              {user ? 'Account Settings' : 'Sign In to Account'}
            </button>
          </div>
        </div>
      )}
    </header>
  );
};
